import axios from 'axios';
import { API_ENDPOINTS } from '../config';

// ============================================================================
//  gateoutApi.js — frontend wrapper for /api/gateout.php
//
//  All gate-out operations route through one PHP endpoint, distinguished by
//  the `action` field. This module exposes typed-feeling helpers per action
//  so the rest of the codebase doesn't have to remember the action strings.
//
//  The PHP endpoint url is API_ENDPOINTS.GATEOUT — added in config.js.
// ============================================================================

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

/**
 * Resolve the gate-out endpoint URL. Three fallbacks:
 *   1. API_ENDPOINTS.GATEOUT — if explicitly added to config.js (recommended)
 *   2. Derive from INVENTORY endpoint by swapping the filename
 *   3. Last-ditch hardcoded path
 */
function gateoutUrl() {
  if (API_ENDPOINTS.GATEOUT) return API_ENDPOINTS.GATEOUT;
  if (API_ENDPOINTS.INVENTORY) {
    return API_ENDPOINTS.INVENTORY.replace(/[a-zA-Z_]+\.php(\?.*)?$/, 'gateout.php');
  }
  return '/api/gateout.php';
}

/** Resolve the recall_upload.php endpoint (mirrors gateoutUrl()). */
function recallUploadUrl() {
  if (API_ENDPOINTS.RECALL_UPLOAD) return API_ENDPOINTS.RECALL_UPLOAD;
  return gateoutUrl().replace(/gateout\.php(\?.*)?$/, 'recall_upload.php');
}

/**
 * Internal: POST { action, ...payload } to the unified gateout endpoint.
 * Returns the parsed response.data, or throws on network error.
 */
async function call(action, payload = {}) {
  const res = await axios.post(gateoutUrl(), { action, ...payload }, getAuthConfig());
  return res.data;
}

// ============================================================================
//  Public actions
// ============================================================================

/**
 * Create a DRAFT gate-out document.
 * @param {Object} doc - { flow_type, gateout_date, gateout_time, destination_dept,
 *                         vehicle_no, driver_name, priority, reason, pallet_ids,
 *                         + shipment-only fields if flow_type === 'SHIPMENT' }
 * @returns {Promise<{ success, doc_id?, totals?, error? }>}
 */
export const createGateOut = (doc) => call('create', doc);

/**
 * Submit a draft. Non-shipment flows auto-execute and return EXECUTED status.
 * Shipment flows return PENDING_APPROVAL and lock pallets to SHIPMENT-PENDING.
 */
export const submitGateOut = (docId) => call('submit', { doc_id: docId });

/**
 * Cancel a DRAFT or PENDING_APPROVAL doc. Unlinks pallets and reverts
 * SHIPMENT-PENDING back to PRODUCED if applicable.
 */
export const cancelGateOut = (docId, reason) =>
  call('cancel', { doc_id: docId, reason });

export const listGateOuts     = (filter) => call('list',     { filter });
export const getGateOutDetail = (docId)   => call('detail',  { doc_id: docId });
export const markReturned     = (payload) => call('mark_returned', payload);
export const directShipment   = (payload) => call('direct_shipment', payload);

// ----------------------------------------------------------------------------
// Bulk return — return one or more pallets from any "out" status
// (REPACK-OUT / RE-PRINTING / RE-PROCESS / QC SAMPLE) back to CS as their
// respective IN status. Supports partial-cartons split.
// ----------------------------------------------------------------------------
export const bulkReturn = (entries, returnDate) =>
  call('bulk_return', { entries, return_date: returnDate });

// ----------------------------------------------------------------------------
// Bulk direct shipment — create one FCL shipment doc covering one or more
// pallets from any "out" status. Same split semantics as bulkReturn.
// ----------------------------------------------------------------------------
export const bulkDirectShipment = (entries, fcl) =>
  call('bulk_direct_shipment', { entries, fcl });

// ----------------------------------------------------------------------------
// Approve / reject a PENDING_APPROVAL shipment doc (in-app, alternate to email)
// ----------------------------------------------------------------------------
export const approveGateOut = (docId) => call('approve', { doc_id: docId });
export const rejectGateOut  = (docId, reason) =>
  call('reject', { doc_id: docId, reason });

// ============================================================================
//  Recall & de-stuff (Cold Store › Recall workflow)
//  Status machine: EXECUTED -> ON_TRANSIT -> REACHED_FACTORY -> RETURNED
// ============================================================================

/**
 * List shipment consignments for the recall tabs.
 * @param {string[]} [status] - defaults to ['EXECUTED','ON_TRANSIT','REACHED_FACTORY']
 * @returns {Promise<{ success, documents }>}
 */
export const listConsignments = (status) =>
  call('list', {
    filter: {
      flow_type: 'SHIPMENT',
      status: status || ['EXECUTED', 'ON_TRANSIT', 'REACHED_FACTORY'],
    },
  });

/**
 * Recall a shipped consignment (EXECUTED -> ON_TRANSIT).
 * @param {Object} payload - { doc_id, recall_date, reason, containers?,
 *                             reference_docs?: [{ file_name, file_path }] }
 *   Upload files first via uploadRecallDocs() and pass the returned list.
 */
export const recallShipment = (payload) => call('recall', payload);

/**
 * Capture the factory gate-in (ON_TRANSIT -> REACHED_FACTORY).
 * @param {number} docId
 * @param {string} gateInAt - 'YYYY-MM-DDTHH:MM' (datetime-local value)
 */
export const gateInShipment = (docId, gateInAt) =>
  call('gate_in', { doc_id: docId, gate_in_at: gateInAt });

/** Manifest (remaining recalled-container lines) for a consignment. */
export const destuffManifest = (docId) => call('destuff_manifest', { doc_id: docId });

/**
 * De-stuff one source line onto one or more plates (cold-store gate-in).
 * @param {Object} payload - { doc_id, source_line_id, gate_in_at, cs_selection,
 *                             wt_per_carton_kg, plates:[{ pallet_id, bin, cartons }] }
 */
export const destuffLine = (payload) => call('destuff_line', payload);

/**
 * Upload recall reference documents (multipart -> recall_upload.php).
 * Call this BEFORE recallShipment and pass the returned files as reference_docs.
 * @param {File[]} files
 * @returns {Promise<{ success, files:[{ file_name, file_path, mime_type, file_size }] }>}
 */
export const uploadRecallDocs = async (files) => {
  const fd = new FormData();
  (files || []).forEach((f) => fd.append('files[]', f));
  const cfg = getAuthConfig();
  const res = await axios.post(recallUploadUrl(), fd, {
    ...cfg,
    headers: { ...cfg.headers, 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

// ============================================================================
//  Process type metadata — single source of truth for the UI
// ============================================================================

export const FLOW_TYPES = {
  REPACK: {
    id: 'REPACK', label: 'Repack-Out', icon: '📦', desc: 'Send for re-packing',
    color: '#ea580c', light: '#fff7ed', prefix: 'RPK',
    after_status: 'REPACK-OUT', after_pill: 'pill-repack-out', default_dept: 'PPC',
  },
  REPRINT: {
    id: 'REPRINT', label: 'Reprint-Out', icon: '🏷', desc: 'Send for label reprint',
    color: '#db2777', light: '#fdf2f8', prefix: 'RPT',
    after_status: 'RE-PRINTING', after_pill: 'pill-reprint', default_dept: 'LABEL',
  },
  REPROCESS: {
    id: 'REPROCESS', label: 'Reprocess-Out', icon: '🔄', desc: 'Send for re-grading',
    color: '#9333ea', light: '#faf5ff', prefix: 'RPS',
    after_status: 'RE-PROCESS', after_pill: 'pill-reprocess', default_dept: 'GRADING',
  },
  QC: {
    id: 'QC', label: 'QC Sample', icon: '🔬', desc: 'Pull cartons for QC',
    color: '#0891b2', light: '#ecfeff', prefix: 'QC',
    after_status: 'QC SAMPLE', after_pill: 'pill-qc', default_dept: 'QC LAB',
  },
  SHIPMENT: {
    id: 'SHIPMENT', label: 'Shipment', icon: '🚚', desc: 'Ship to buyer',
    color: '#7c3aed', light: '#f5f3ff', prefix: 'FCL',
    after_status: 'SHIPPED', after_pill: 'pill-shipped', default_dept: 'EXPORT',
  },
};

/**
 * Statuses that allow a pallet to be selected for a given flow.
 * Mirrors getEligibleStatuses() in gateout.php.
 */
export const ELIGIBLE_STATUSES = {
  REPACK:    ['PRODUCED', 'REPACK-IN', 'REPROCESS-IN', 'QC-SAMPLE-IN'],
  REPRINT:   ['PRODUCED', 'REPACK-IN', 'REPROCESS-IN', 'QC-SAMPLE-IN'],
  REPROCESS: ['PRODUCED', 'REPACK-IN', 'REPROCESS-IN', 'QC-SAMPLE-IN'],
  QC:        ['PRODUCED', 'REPACK-IN', 'REPROCESS-IN', 'QC-SAMPLE-IN'],
  SHIPMENT:  ['PRODUCED', 'REPACK-IN', 'REPROCESS-IN', 'QC-SAMPLE-IN',
              'REPACK-OUT', 'RE-PRINTING', 'RE-PROCESS'],
};

export const isPalletEligibleForFlow = (palletStatus, flowType) =>
  (ELIGIBLE_STATUSES[flowType] || []).includes(palletStatus);
